import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { 
  Heart, Music, Star, Users, Lightbulb, Award,
  ArrowRight, Phone, CheckCircle
} from 'lucide-react';

const values = [
  {
    icon: Heart,
    title: "Bienveillance",
    description: "Chaque élève est accueilli avec douceur et encouragé dans sa progression, sans jugement."
  },
  {
    icon: Star,
    title: "Plaisir",
    description: "Le plaisir de la musique est la priorité. Apprendre doit rester une source de joie."
  },
  {
    icon: Lightbulb,
    title: "Créativité",
    description: "Stimuler l'imagination et encourager l'expression personnelle de chaque élève."
  },
  {
    icon: Award,
    title: "Rigueur",
    description: "Une rigueur musicale plaisante qui permet une progression solide et durable."
  }
];

const methodSteps = [
  {
    number: "01",
    title: "Écoute & Découverte",
    description: "Nous faisons connaissance : vos goûts, vos envies, vos objectifs. Nous évaluons votre niveau actuel."
  },
  {
    number: "02",
    title: "Programme Personnalisé",
    description: "Nous construisons un programme sur mesure, adapté à votre rythme et vos préférences musicales."
  },
  {
    number: "03",
    title: "Apprentissage Progressif",
    description: "Chaque séance alterne technique et plaisir, avec des morceaux que vous aimez."
  },
  {
    number: "04",
    title: "Valorisation & Progrès",
    description: "Les progrès sont célébrés ! Petites victoires et grandes avancées rythment le parcours."
  }
];

const firstLessonSteps = [
  "Accueil chaleureux et discussion pour faire connaissance",
  "Évaluation du niveau (pour ceux qui ont déjà des bases)",
  "Découverte de l'instrument et premiers gestes",
  "Discussion sur les goûts musicaux et les objectifs",
  "Présentation de la méthode et du fonctionnement",
  "Questions et réponses sur l'organisation des cours"
];

export default function AboutPage() {
  return (
    <div className="min-h-screen pt-20" data-testid="about-page">
      <Helmet>
        <title>À Propos - École de Musique Pour Tous à Périgueux | Charlotte et son équipe</title>
        <meta name="description" content="Découvrez l'École de Musique Pour Tous à Périgueux. Charlotte et son équipe enseignent le piano, la guitare, le chant et l'éveil musical avec passion et bienveillance." />
        <link rel="canonical" href="https://ecoledemusiqueperigueux.fr/a-propos" />
      </Helmet>
      {/* Hero */}
      <section className="py-16 bg-gradient-to-br from-cream to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-block px-4 py-2 bg-burgundy/10 text-burgundy rounded-full text-sm font-semibold mb-4">
                École de musique à Périgueux
              </span>
              <h1 className="font-dancing text-5xl sm:text-6xl text-burgundy mb-6">
                Cours de musique à Périgueux avec Charlotte et son équipe
              </h1>
              <p className="text-gray-600 text-lg mb-6 leading-relaxed font-nunito">
                Bienvenue à l'École de Musique Pour Tous ! Je suis Charlotte Naboulet, enseignante référente 
                et professeur de piano à Périgueux, dans le quartier Clos Chassaing. Avec mon équipe, 
                nous transmettons l'amour de la musique aux petits et grands dans une ambiance chaleureuse.
              </p>
              <p className="text-gray-600 mb-8 leading-relaxed font-nunito">
                Nous proposons des cours de piano, guitare, chant et éveil musical à Périgueux pour tous les âges et niveaux. 
                Notre pédagogie allie bienveillance, plaisir et rigueur. Premier cours d'essai gratuit !
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-burgundy text-white rounded-full font-semibold hover:bg-burgundy-600 transition-all"
                  data-testid="about-cta-btn"
                >
                  Nous contacter
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <a
                  href="tel:+33649523778"
                  className="inline-flex items-center gap-2 px-6 py-3 border-2 border-burgundy text-burgundy rounded-full font-semibold hover:bg-burgundy hover:text-white transition-all"
                  data-testid="about-phone-btn"
                >
                  <Phone className="w-5 h-5" />
                  06 49 52 37 78
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="absolute -top-4 -right-4 w-full h-full bg-coral/10 rounded-3xl" />
              <img
                src="https://customer-assets-7cd3h4nn.emergentagent.net/job_cours-piano-guitare/artifacts/celizo8d_Sans%20titre-11.jpg"
                alt="Cours de piano à Périgueux - École de Musique Pour Tous"
                className="relative rounded-3xl shadow-xl w-full h-auto object-cover"
                loading="lazy"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-block px-4 py-2 bg-teal/10 text-teal rounded-full text-sm font-semibold mb-4">
                Nos Valeurs
              </span>
              <h2 className="font-dancing text-4xl sm:text-5xl text-burgundy mb-4">
                Ce qui guide notre enseignement
              </h2>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value) => {
              const Icon = value.icon;
              return (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="bg-cream rounded-3xl p-6 text-center"
                >
                  <div className="w-14 h-14 bg-burgundy/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-7 h-7 text-burgundy" />
                  </div>
                  <h3 className="font-nunito font-bold text-lg text-gray-800 mb-2">{value.title}</h3>
                  <p className="text-gray-600 text-sm">{value.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Method */}
      <section className="py-20 bg-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-block px-4 py-2 bg-coral/10 text-coral rounded-full text-sm font-semibold mb-4">
                Notre Méthode
              </span>
              <h2 className="font-dancing text-4xl sm:text-5xl text-burgundy mb-4">
                Une approche en 4 étapes
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto font-nunito">
                Un accompagnement personnalisé du premier contact jusqu'à la maîtrise de votre instrument.
              </p>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {methodSteps.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="bg-white rounded-3xl p-6 shadow-sm h-full">
                  <span className="font-dancing text-5xl text-coral/30">{step.number}</span>
                  <h3 className="font-nunito font-bold text-lg text-burgundy mb-2 -mt-2">{step.title}</h3>
                  <p className="text-gray-600 text-sm">{step.description}</p>
                </div>
                {index < methodSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-0.5 bg-coral/30" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* First Lesson */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="absolute -top-4 -left-4 w-full h-full bg-teal/10 rounded-3xl" />
              <img
                src="https://customer-assets-7cd3h4nn.emergentagent.net/job_cours-piano-guitare/artifacts/t2x12vzi_1788262479965.jpg"
                alt="École de musique à Périgueux - Accueil des élèves"
                className="relative rounded-3xl shadow-xl w-full h-auto object-cover"
                loading="lazy"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-block px-4 py-2 bg-teal/10 text-teal rounded-full text-sm font-semibold mb-4">
                Déroulé
              </span>
              <h2 className="font-dancing text-4xl sm:text-5xl text-burgundy mb-6">
                Votre premier cours
              </h2>
              <p className="text-gray-600 mb-6 font-nunito">
                Le premier cours est gratuit et sans engagement. C'est un moment de découverte 
                mutuelle où nous faisons connaissance et où nous vous présentons notre méthode.
              </p>
              <ul className="space-y-4 mb-8">
                {firstLessonSteps.map((step) => (
                  <li key={step} className="flex items-start gap-3 text-gray-700">
                    <div className="w-6 h-6 bg-teal/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="w-4 h-4 text-teal" />
                    </div>
                    <span className="font-nunito text-sm">{step}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-6 py-3 bg-teal text-white rounded-full font-semibold hover:bg-teal-600 transition-all"
                data-testid="first-lesson-cta"
              >
                Réserver mon essai gratuit
                <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="py-20 bg-burgundy text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Music className="w-12 h-12 mx-auto mb-6 opacity-80" />
            <blockquote className="font-dancing text-3xl sm:text-4xl mb-8 leading-relaxed">
              "La musique donne une âme à nos cœurs et des ailes à la pensée."
            </blockquote>
            <p className="text-white/70 font-nunito">— Platon</p>
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-cream">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-dancing text-4xl sm:text-5xl text-burgundy mb-6">
              Envie de nous rencontrer ?
            </h2>
            <p className="text-gray-600 text-lg mb-8 font-nunito">
              Réservez votre cours d'essai gratuit et découvrons ensemble votre potentiel musical.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-8 py-4 bg-burgundy text-white rounded-full font-bold hover:bg-burgundy-600 transition-all"
                data-testid="about-final-cta"
              >
                Réserver un essai gratuit
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/cours"
                className="inline-flex items-center gap-2 px-8 py-4 border-2 border-burgundy text-burgundy rounded-full font-bold hover:bg-burgundy hover:text-white transition-all"
                data-testid="about-courses-link"
              >
                Découvrir les cours
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
